import Phaser from "phaser";
import { MainScene } from "./scenes/MainScene";
import { CharacterCreator } from "./world/CharacterCreator";
import { Appearance, DEFAULT_APPEARANCE } from "./world/CharacterAvatar";

interface Member {
  id: string;
  username: string;
  nickname: string;
  member_id: string;
}

const loginScreen = document.getElementById("login") as HTMLDivElement;

const API_BASE = (() => {
  if (location.port === "5173") return `${location.protocol}//${location.hostname}:2567`;
  return "";
})();

let activeTab: "login" | "register" = "login";

function showError(msg: string) {
  const el = document.getElementById("err") as HTMLDivElement | null;
  if (el) el.textContent = msg;
}

function clear(el: Element) {
  while (el.firstChild) el.removeChild(el.firstChild);
}

function setActiveTab(t: "login" | "register") {
  activeTab = t;
  const lt = document.getElementById("tab-login") as HTMLButtonElement;
  const rt = document.getElementById("tab-register") as HTMLButtonElement;
  if (lt && rt) {
    lt.classList.toggle("active", t === "login");
    rt.classList.toggle("active", t === "register");
  }
  const lf = document.getElementById("login-form") as HTMLFormElement;
  const rf = document.getElementById("register-form") as HTMLFormElement;
  if (lf) lf.style.display = t === "login" ? "block" : "none";
  if (rf) rf.style.display = t === "register" ? "block" : "none";
  showError("");
}

async function attemptLogin(username: string, password: string) {
  showError("");
  const r = await fetch(`${API_BASE}/api/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  if (r.status === 401) {
    showError("Wrong username or password.");
    return;
  }
  if (!r.ok) {
    showError("Login error. Try again.");
    return;
  }
  const data = await r.json();
  storeSession(data.token, data.user, data.appearance ?? DEFAULT_APPEARANCE);
  enterGame(data.token, data.user, data.appearance ?? DEFAULT_APPEARANCE);
}

async function attemptRegister(username: string, password: string, nickname: string) {
  showError("");
  const r = await fetch(`${API_BASE}/api/auth/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password, nickname }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({ error: "register failed" }));
    showError(err.error ?? "register failed");
    return;
  }
  const data = await r.json();
  storeSession(data.token, data.user, data.appearance ?? DEFAULT_APPEARANCE);
  // New users → character creator first
  loginScreen.style.display = "none";
  new CharacterCreator(data.token, data.appearance ?? DEFAULT_APPEARANCE, (final) => {
    enterGame(data.token, data.user, final);
  });
}

function storeSession(token: string, user: Member, appearance: Appearance) {
  sessionStorage.setItem("one-heart-token", token);
  sessionStorage.setItem("one-heart-user", JSON.stringify(user));
  sessionStorage.setItem("one-heart-appearance", JSON.stringify(appearance));
}

function enterGame(token: string, user: Member, appearance: Appearance) {
  loginScreen.style.display = "none";
  // Show the in-game "Edit look" button (hidden until now). Wire it once.
  const editBtn = document.getElementById("edit-look-btn") as HTMLButtonElement | null;
  if (editBtn) {
    editBtn.style.display = "block";
    if (!editBtn.dataset.wired) {
      editBtn.dataset.wired = "1";
      // Bridge HTML → Phaser scene via CustomEvent. Phaser global is bundled
      // (no window.Phaser.GAMES) so we can't reach the scene by lookup. The
      // scene listens for 'one-heart:edit-look' in MainScene.create().
      editBtn.onclick = () => window.dispatchEvent(new Event("one-heart:edit-look"));
    }
  }
  // ── Mobile virtual joystick + action buttons ──
  // Wires up touch input → CustomEvents that MainScene listens for. The
  // joystick produces a normalized (dx, dy) every frame; action buttons
  // emit one-shot key intents. Whole block is a no-op on desktop because
  // the elements are display:none via @media (pointer: coarse).
  setupMobileControls();

  // member object — kept for back-compat with MainScene which expects { id, name, tier }
  const member = { id: user.member_id, name: user.nickname, tier: "guest" };
  const game = new Phaser.Game({
    type: Phaser.AUTO,
    parent: "game",
    backgroundColor: "#1a1430",
    width: window.innerWidth,
    height: window.innerHeight,
    scale: {
      mode: Phaser.Scale.RESIZE,
      autoCenter: Phaser.Scale.CENTER_BOTH,
    },
    scene: [new MainScene(token, member, appearance)],
    pixelArt: true,
    physics: {
      default: "arcade",
      arcade: { gravity: { x: 0, y: 0 }, debug: false },
    },
  });
  // Expose globally for Playwright debugging.
  (window as any).GAME = game;
}

window.addEventListener("DOMContentLoaded", () => {
  // Tab switching
  document.getElementById("tab-login")?.addEventListener("click", () => setActiveTab("login"));
  document.getElementById("tab-register")?.addEventListener("click", () => setActiveTab("register"));

  // Login form
  const loginForm = document.getElementById("login-form") as HTMLFormElement;
  loginForm?.addEventListener("submit", (e) => {
    e.preventDefault();
    const u = (document.getElementById("li-username") as HTMLInputElement).value.trim();
    const p = (document.getElementById("li-password") as HTMLInputElement).value;
    if (!u || !p) return showError("Enter username and password.");
    attemptLogin(u, p);
  });

  // Register form
  const regForm = document.getElementById("register-form") as HTMLFormElement;
  regForm?.addEventListener("submit", (e) => {
    e.preventDefault();
    const u = (document.getElementById("rg-username") as HTMLInputElement).value.trim();
    const p = (document.getElementById("rg-password") as HTMLInputElement).value;
    const p2 = (document.getElementById("rg-password2") as HTMLInputElement).value;
    const n = (document.getElementById("rg-nickname") as HTMLInputElement).value.trim();
    if (!u || !p || !n) return showError("All fields required.");
    if (p !== p2) return showError("Passwords don't match.");
    if (p.length < 6) return showError("Password must be ≥ 6 chars.");
    if (!/^[a-z0-9_]+$/i.test(u)) return showError("Username: letters/numbers/underscore only.");
    attemptRegister(u, p, n);
  });

  // Auto-resume if we have a saved session
  const token = sessionStorage.getItem("one-heart-token");
  const userRaw = sessionStorage.getItem("one-heart-user");
  const appRaw = sessionStorage.getItem("one-heart-appearance");
  if (token && userRaw) {
    try {
      const user = JSON.parse(userRaw);
      const appearance = appRaw ? JSON.parse(appRaw) : DEFAULT_APPEARANCE;
      enterGame(token, user, appearance);
    } catch {
      /* fall through */
    }
  }

  setActiveTab("login");
});

/**
 * Wire the mobile virtual joystick (#m-joystick) + action buttons (#m-actions).
 *
 *  Joystick model: a 132px circular base with a draggable inner knob. Touch
 *  inside the base registers as "active"; on each touchmove we compute the
 *  knob offset from base centre and emit a normalized direction CustomEvent.
 *  Touchend resets the knob and emits a (0, 0) stop.
 *
 *  Action buttons each fire a `one-heart:action` CustomEvent with `key` set
 *  to the button's data-key. MainScene's input handlers translate these to
 *  the same actions as the keyboard equivalents (E = interact, V = wave,
 *  T = chat, space = hop).
 */
function setupMobileControls() {
  const joystick = document.getElementById("m-joystick");
  const knob = joystick?.querySelector<HTMLDivElement>(".knob");
  const base = joystick?.querySelector<HTMLDivElement>(".base");
  if (joystick && knob && base) {
    let activeId: number | null = null;
    let centreX = 0, centreY = 0;
    const RADIUS = 38;             // max knob excursion in px

    const touchStart = (e: TouchEvent) => {
      const t = e.changedTouches[0];
      const rect = base.getBoundingClientRect();
      centreX = rect.left + rect.width / 2;
      centreY = rect.top + rect.height / 2;
      activeId = t.identifier;
      e.preventDefault();
    };
    const touchMove = (e: TouchEvent) => {
      if (activeId === null) return;
      for (const t of Array.from(e.changedTouches)) {
        if (t.identifier !== activeId) continue;
        let dx = t.clientX - centreX;
        let dy = t.clientY - centreY;
        const dist = Math.hypot(dx, dy);
        if (dist > RADIUS) { dx = dx / dist * RADIUS; dy = dy / dist * RADIUS; }
        knob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
        // Normalize to [-1, 1]
        const nx = dx / RADIUS;
        const ny = dy / RADIUS;
        window.dispatchEvent(new CustomEvent("one-heart:move", { detail: { x: nx, y: ny } }));
        e.preventDefault();
      }
    };
    const touchEnd = (e: TouchEvent) => {
      for (const t of Array.from(e.changedTouches)) {
        if (t.identifier !== activeId) continue;
        activeId = null;
        knob.style.transform = "translate(-50%, -50%)";
        window.dispatchEvent(new CustomEvent("one-heart:move", { detail: { x: 0, y: 0 } }));
        e.preventDefault();
      }
    };

    joystick.addEventListener("touchstart", touchStart, { passive: false });
    joystick.addEventListener("touchmove",  touchMove,  { passive: false });
    joystick.addEventListener("touchend",   touchEnd,   { passive: false });
    joystick.addEventListener("touchcancel", touchEnd,  { passive: false });
  }

  const actions = document.getElementById("m-actions");
  if (actions) {
    actions.querySelectorAll<HTMLButtonElement>("button[data-key]").forEach((btn) => {
      const key = btn.dataset.key!;
      const fire = (ev: Event) => {
        ev.preventDefault();
        window.dispatchEvent(new CustomEvent("one-heart:action", { detail: { key } }));
      };
      // touchstart for instant response (vs click which waits 300ms on iOS)
      btn.addEventListener("touchstart", fire, { passive: false });
      btn.addEventListener("click", fire);          // fallback for desktop testing
    });
  }
}
